/**
 * ServerList.tsx - 服务器管理列表页面
 *
 * 功能概述：
 * - 展示所有已注册设备的列表（通过 GET /api/servers 获取）
 * - 支持按关键字搜索（设备ID / 备注 / 管理IP）
 * - 支持按 MQTT 状态筛选（在线/离线/全部）
 * - 支持按应用版本筛选（动态从数据中提取选项）
 * - 支持按频道筛选（仅 admin，频道字段不在列表中展示）
 * - 支持行内编辑备注（点击备注文字直接编辑）
 * - 支持删除设备（带二次确认弹窗）
 * - 操作列提供：WebShell / 设备配置 / 生成外部链接 / 详情 / 删除
 *
 * 数据来源：
 * - GET /api/servers → server_list 表
 * - systemInfo 字段来自 MQTT client/+/sysinfo 上报（JSON 格式存入 server_list.system_info）
 */

import React, { useState, useEffect, FC, useMemo } from 'react';
import { Table, Button, Tag, Space, Input, Modal, Form, message, Typography, Select } from 'antd';
import { SearchOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getServerList, deleteServer, updateServer } from '../api/server';
import { createTerminalTicket } from '../api/terminal';
import { getDistinctChannels } from '../api/channel';
import config from '../config';
import { TimeUtil } from '../utils/timeUtil';
import { handleApiError } from '../utils/errorHandler';
import GenerateExternalLinkModal from '../components/GenerateExternalLinkModal';
import { useAuthStore } from '../store/authStore';

const { Title } = Typography;

/**
 * Server - 服务器列表项的数据类型
 * 对应后端 GET /api/servers 返回的单条记录结构
 */
interface Server {
  id: number;           // 数据库自增主键
  deviceId: string;     // 设备唯一标识（MQTT 注册时写入）
  lineStatus: string;   // MQTT 连接状态：'online' | 'offline'
  lineStatusFrp?: string; // FRP 隧道状态：'online' | 'offline'（定时轮询更新）
  proxySshPort: string; // FRP 代理 SSH 端口
  remark: string;       // 备注（可在列表页行内编辑）
  manageIp: string;     // 管理 IP
  addTime: string;      // 设备首次注册时间
  lastUpdateTime: string; // 最后数据更新时间
  channelName?: string; // 设备所属频道名称（用于多租户隔离）
  systemInfo?: {        // 设备系统信息（来自 MQTT sysinfo 上报，可能为空）
    osVer?: string;         // 操作系统版本
    appVer?: string;        // 应用版本（如 'pute'）
    kernel?: string;        // 内核版本
    version?: string;       // 系统发行版（如 'CentOS Linux 7'）
    edge_version?: string;  // Edge 组件版本
    llcp_version?: string;  // LLCP 组件版本（可能为 null）
    plugin_version?: string; // 插件版本
  };
}

/**
 * EditableCellProps - 行内编辑备注组件的属性类型
 */
interface EditableCellProps {
  text: string;           // 当前备注文字
  record: Server;         // 当前行的完整服务器数据
  fetchServers: () => void; // 保存成功后刷新列表的回调
}

/**
 * EditableCell - 行内可编辑备注组件
 *
 * 交互逻辑：
 * - 默认显示文字，点击后变为输入框
 * - 按回车或失焦时自动保存（调用 PATCH /api/servers/:deviceId）
 * - 保存成功后刷新整个列表
 */
const EditableCell: FC<EditableCellProps> = ({ text, record, fetchServers }) => {
  const [editing, setEditing] = useState(false); // 是否处于编辑状态
  const [value, setValue] = useState(text);       // 输入框当前值

  const handleSave = async () => {
    try {
      await updateServer(record.deviceId, { remark: value });
      setEditing(false);
      fetchServers(); // 保存后刷新列表
      message.success('备注更新成功');
    } catch (error) {
      handleApiError(error, '更新备注失败');
    }
  };

  // 编辑状态：显示输入框
  return editing ? (
    <Input
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onPressEnter={handleSave}  // 按回车保存
      onBlur={handleSave}        // 失焦保存
      autoFocus
    />
  ) : (
    // 非编辑状态：点击文字进入编辑
    <div onClick={() => setEditing(true)} style={{ cursor: 'pointer' }}>
      {text || '-'}
    </div>
  );
};

/**
 * ServerList - 服务器管理列表页主组件
 */
const ServerList: React.FC = () => {
  // ── 状态定义 ──────────────────────────────────────────────────────────────
  const [servers, setServers] = useState<Server[]>([]);          // 原始服务器列表（来自接口）
  const [loading, setLoading] = useState<boolean>(true);         // 表格加载中状态
  const [searchText, setSearchText] = useState<string>('');      // 搜索框文字
  const [statusFilter, setStatusFilter] = useState<string>('all');   // 状态筛选值：'all' | 'online' | 'offline'
  const [appVerFilter, setAppVerFilter] = useState<string>('all');   // 应用版本筛选值
  const [channelFilter, setChannelFilter] = useState<string>('all'); // 频道筛选值
  const [distinctChannels, setDistinctChannels] = useState<string[]>([]); // 所有唯一频道名称
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false); // 添加服务器弹窗（当前已禁用）
  const [externalLinkDeviceId, setExternalLinkDeviceId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const authStore = useAuthStore();

  // ── 计算属性：动态提取应用版本选项 ────────────────────────────────────────
  // 从已加载的服务器列表中提取所有不重复的 appVer 值，作为版本筛选下拉选项
  // useMemo 确保只在 servers 变化时重新计算，避免每次渲染都重新遍历
  const appVerOptions = useMemo(() => {
    const versions = servers
      .map(s => s.systemInfo?.appVer)
      .filter((v): v is string => !!v); // 过滤掉 null/undefined/空字符串
    return Array.from(new Set(versions)).sort(); // 去重并排序
  }, [servers]);

  // ── 生命周期：页面加载时获取服务器列表和频道列表 ─────────────────────────
  useEffect(() => {
    fetchServers();
    
    // 仅 admin 加载频道列表
    if (authStore.role === 'admin') {
      getDistinctChannels()
        .then((res: { data: string[] }) => setDistinctChannels(res.data))
        .catch((err: any) => console.error('获取频道列表失败:', err));
    }
  }, [authStore.role]);

  /**
   * 获取服务器列表
   * 调用 GET /api/servers，将结果存入 servers state
   */
  const fetchServers = async () => {
    try {
      setLoading(true);
      const { data } = await getServerList();
      setServers(data);
    } catch (error) {
      handleApiError(error, '获取服务器列表失败');
    } finally {
      setLoading(false);
    }
  };

  /** 搜索框输入变化时更新搜索文字 */
  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  // ── 过滤逻辑：四个条件同时生效（AND 关系）────────────────────────────────
  const filteredServers = servers.filter(server => {
    // 条件1：文本搜索（设备ID / 备注 / 管理IP，任意匹配即可）
    const matchesSearch =
      (server.deviceId || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (server.remark || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (server.manageIp || '').toLowerCase().includes(searchText.toLowerCase());

    // 条件2：MQTT 状态筛选（'all' 表示不筛选）
    const matchesStatus =
      statusFilter === 'all' || server.lineStatus === statusFilter;

    // 条件3：应用版本筛选（'all' 表示不筛选）
    const matchesAppVer =
      appVerFilter === 'all' || (server.systemInfo?.appVer || '') === appVerFilter;

    // 条件4：频道筛选（仅 admin 有效，'all' 表示不筛选）
    const matchesChannel =
      channelFilter === 'all' || server.channelName === channelFilter;

    return matchesSearch && matchesStatus && matchesAppVer && matchesChannel;
  });

  /** 关闭添加服务器弹窗 */
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  /**
   * 添加服务器表单提交
   * 注意：服务器只能通过 MQTT 自动注册，此处禁止手动添加
   */
  const handleAddServer = async (values: any) => {
    message.error('服务器只能通过MQTT服务注册');
    setIsModalVisible(false);
  };

  /**
   * 删除服务器
   * 先弹出确认对话框，确认后调用 DELETE /api/servers/:deviceId
   * 删除成功后刷新列表
   */
  const apiOrigin = () => config.API_BASE_URL.replace(/\/api\/?$/i, '');

  const openWebShell = async (deviceId: string) => {
    const token = localStorage.getItem('token');
    if (token) {
      window.open(
        `${apiOrigin()}/api/terminal?device_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`,
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }
    try {
      const { data } = await createTerminalTicket({ device_id: deviceId });
      window.open(data.ssh_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      handleApiError(e);
      message.error('获取 WebShell 链接失败');
    }
  };

  const openDeviceWeb = async (deviceId: string) => {
    const token = localStorage.getItem('token');
    if (token) {
      window.open(
        `${apiOrigin()}/api/terminal?web_id=${encodeURIComponent(deviceId)}&token=${encodeURIComponent(token)}`,
        '_blank',
        'noopener,noreferrer',
      );
      return;
    }
    try {
      const { data } = await createTerminalTicket({ device_id: deviceId });
      window.open(data.web_url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      handleApiError(e);
      message.error('获取设备 Web 入口失败');
    }
  };

  const handleDeleteServer = async (deviceId: string) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个服务器吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteServer(deviceId);
          message.success('服务器删除成功');
          fetchServers();
        } catch (error) {
          handleApiError(error, '服务器删除失败');
        }
      },
    });
  };

  // ── 表格列定义 ────────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      // 设备唯一标识，由设备首次 MQTT 上线时自动注册写入
      title: '设备ID',
      dataIndex: 'deviceId',
      key: 'deviceId',
    },
    {
      // MQTT 连接状态：绿色=在线，红色=离线
      // 数据来源：MQTT client/+/status 主题上报
      title: 'MQTT状态',
      dataIndex: 'lineStatus',
      key: 'lineStatus',
      render: (status: string) => (
        <Tag color={status === 'online' ? 'green' : 'red'}>
          {status === 'online' ? '在线' : '离线'}
        </Tag>
      ),
    },
    {
      // FRP 隧道状态：绿色=在线，红色=离线
      // 数据来源：后端每分钟轮询 FRP API 更新
      title: 'FRP状态',
      dataIndex: 'lineStatusFrp',
      key: 'lineStatusFrp',
      render: (status: string) => {
        const frpStatus = status || 'offline'; // 未知状态默认显示离线
        return (
          <Tag color={frpStatus === 'online' ? 'green' : 'red'}>
            {frpStatus === 'online' ? '在线' : '离线'}
          </Tag>
        );
      },
    },
    {
      // 备注：点击可行内编辑，失焦或回车自动保存
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      render: (text: string, record: Server) => (
        <EditableCell text={text} record={record} fetchServers={fetchServers} />
      ),
    },
    {
      // 设备首次上线（注册）时间
      title: '上线时间',
      dataIndex: 'addTime',
      key: 'addTime',
      render: (text: string) => TimeUtil.formatServerTime(text),
    },
    {
      // 最后一次数据更新时间（任意 MQTT 消息处理后更新）
      title: '最后更新',
      dataIndex: 'lastUpdateTime',
      key: 'lastUpdateTime',
      render: (text: string) => TimeUtil.formatServerTime(text),
    },
    {
      // 应用版本，来自 systemInfo.appVer（MQTT sysinfo 上报）
      title: '应用版本',
      key: 'appVer',
      render: (_: any, record: Server) => record.systemInfo?.appVer || '-',
    },
    {
      // Edge 组件版本，来自 systemInfo.edge_version
      title: 'Edge版本',
      key: 'edge_version',
      render: (_: any, record: Server) => record.systemInfo?.edge_version || '-',
    },
    {
      // LLCP 组件版本，来自 systemInfo.llcp_version（可能为 null）
      title: 'LLCP版本',
      key: 'llcp_version',
      render: (_: any, record: Server) => record.systemInfo?.llcp_version || '-',
    },
    {
      // 插件版本，来自 systemInfo.plugin_version
      title: '插件版本',
      key: 'plugin_version',
      render: (_: any, record: Server) => record.systemInfo?.plugin_version || '-',
    },
    {
      // 操作列：WebShell / 设备配置 / 详情 / 删除
      title: '操作',
      key: 'action',
      render: (text: string, record: Server) => (
        <Space size="middle">
          {/* WebShell：打开远程终端，传入 device_id */}
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => void openWebShell(record.deviceId)}
          >
            WebShell
          </Button>
          {/* 设备配置：打开设备 Web 配置页，传入 web_id */}
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => void openDeviceWeb(record.deviceId)}
          >
            设备配置
          </Button>
          <Button
            type="default"
            icon={<LinkOutlined />}
            size="small"
            onClick={() => setExternalLinkDeviceId(record.deviceId)}
          >
            生成外部链接
          </Button>
          {/* 详情：跳转到 /servers/:deviceId 详情页 */}
          <Button
            type="default"
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/servers/${record.deviceId}`)}
          >
            详情
          </Button>
          {/* 删除：带二次确认弹窗 */}
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDeleteServer(record.deviceId)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  // ── 页面渲染 ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* 顶部工具栏：标题 + 搜索/筛选区域 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>服务器管理</Title>
        <Space wrap>
          {/* 文本搜索框：支持设备ID / 备注 / 管理IP */}
          <Input
            placeholder="搜索服务器"
            prefix={<SearchOutlined />}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 200 }}
          />
          {/* MQTT 状态筛选下拉 */}
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 120 }}
            options={[
              { label: '全部状态', value: 'all' },
              { label: '在线', value: 'online' },
              { label: '离线', value: 'offline' },
            ]}
          />
          {/* 应用版本筛选下拉：选项从已加载数据中动态提取，有几个版本显示几个 */}
          <Select
            value={appVerFilter}
            onChange={(v) => setAppVerFilter(v)}
            style={{ width: 160 }}
            options={[
              { label: '全部版本', value: 'all' },
              ...appVerOptions.map(v => ({ label: v, value: v })),
            ]}
            placeholder="应用版本"
          />
          {/* 频道筛选下拉：仅 admin 可见，显示所有唯一频道名称 */}
          {authStore.role === 'admin' && distinctChannels.length > 0 && (
            <Select
              value={channelFilter}
              onChange={setChannelFilter}
              style={{ width: 150 }}
              options={[
                { label: '全部频道', value: 'all' },
                ...distinctChannels.map(channel => ({ label: channel, value: channel })),
              ]}
              placeholder="选择频道"
            />
          )}
        </Space>
      </div>
      
      <Table
        columns={columns}
        dataSource={filteredServers}
        rowKey="id"
        loading={loading}
      />
      
      <Modal
        title="添加服务器"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddServer}
        >
          <Form.Item
            name="deviceId"
            label="设备ID"
            rules={[{ required: true, message: '请输入设备ID' }]}
          >
            <Input placeholder="请输入设备ID" />
          </Form.Item>
          
          <Form.Item
            name="remark"
            label="备注"
            initialValue=""
          >
            <Input placeholder="请输入备注" />
          </Form.Item>
          
          <Form.Item>
            <Button type="primary" htmlType="submit">
              提交
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <GenerateExternalLinkModal
        open={externalLinkDeviceId !== null}
        deviceId={externalLinkDeviceId}
        onClose={() => setExternalLinkDeviceId(null)}
      />
    </div>
  );
};

export default ServerList;
